import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Room,
  VolumeUp,
  PlayArrow,
  LocationOn,
  ContentCopy,
} from '@mui/icons-material';
import { useZones, useCreateZone, useUpdateZone, useDeleteZone, useDuplicateZone } from '../../hooks/useScheduling';
import { DuplicateZoneDialog } from '../../components/DuplicateToOrgDialog';
import { useSites } from '../../hooks/useFleet';
import type { Zone } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
];

const ZonesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingZone, setDuplicatingZone] = useState<Zone | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  // Fetch sites and zones filtered by organization
  const { data: sites } = useSites({ organization_id: orgFilter });
  const { data: zones, isLoading, error } = useZones({ organization_id: orgFilter });

  const createMutation = useCreateZone();
  const updateMutation = useUpdateZone();
  const deleteMutation = useDeleteZone();
  const duplicateMutation = useDuplicateZone();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    site_id: '',
    color: '#06b6d4',
    default_volume: 80,
    crossfade_duration: 3,
    is_active: true,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      site_id: sites?.data?.[0]?.id || '',
      color: '#06b6d4',
      default_volume: 80,
      crossfade_duration: 3,
      is_active: true,
    });
    setEditingZone(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (zone: Zone) => {
    setFormData({
      name: zone.name,
      description: zone.description || '',
      site_id: zone.site_id,
      color: zone.color,
      default_volume: zone.default_volume,
      crossfade_duration: zone.crossfade_duration,
      is_active: zone.is_active,
    });
    setEditingZone(zone);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingZone) {
        await updateMutation.mutateAsync({
          id: editingZone.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save zone:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this zone?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleOpenDuplicate = (zone: Zone) => {
    setDuplicatingZone(zone);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (params: { name?: string; targetSiteId?: string }) => {
    if (!duplicatingZone) return;
    await duplicateMutation.mutateAsync({
      zoneId: duplicatingZone.id,
      name: params.name,
      targetSiteId: params.targetSiteId,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load zones: {(error as Error)?.message}
      </Alert>
    );
  }

  const filteredZones = zones?.filter(z =>
    z.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    z.site_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.zones}
        actionButton={{
          label: 'New Zone',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search zones..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Zones Grid */}
      <Grid container spacing={2}>
        {filteredZones.map((zone) => (
          <Grid item xs={12} sm={6} md={4} key={zone.id}>
            <Card
              sx={{
                border: '2px solid',
                borderColor: zone.color,
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                opacity: zone.is_active ? 1 : 0.6,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Room sx={{ color: zone.color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {zone.name}
                  </Typography>
                  <Tooltip title="Duplicate to Site">
                    <IconButton size="small" onClick={() => handleOpenDuplicate(zone)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleOpenEdit(zone)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(zone.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {zone.site_name} - {zone.organization_name}
                  </Typography>
                </Box>

                {zone.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {zone.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip
                    size="small"
                    icon={<VolumeUp />}
                    label={`${zone.default_volume}%`}
                  />
                  <Chip
                    size="small"
                    label={`${zone.crossfade_duration}s fade`}
                  />
                  {zone.is_active ? (
                    <Chip
                      size="small"
                      icon={<PlayArrow />}
                      label="Active"
                      sx={{ bgcolor: '#10b98120', color: '#10b981' }}
                    />
                  ) : (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {filteredZones.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Room sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No zones yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create zones to define audio areas within your venues
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate}>
                Create Zone
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingZone ? 'Edit Zone' : 'Create New Zone'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth required>
              <InputLabel>Site</InputLabel>
              <Select
                value={formData.site_id}
                label="Site"
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
              >
                {sites?.data?.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.name} - {site.organization_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32, height: 32, borderRadius: 1, bgcolor: color, cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Default Volume: {formData.default_volume}%
              </Typography>
              <Slider
                value={formData.default_volume}
                onChange={(_, value) => setFormData({ ...formData, default_volume: value as number })}
                min={0}
                max={100}
                valueLabelDisplay="auto"
              />
            </Box>
            <TextField
              label="Crossfade Duration (seconds)"
              type="number"
              value={formData.crossfade_duration}
              onChange={(e) => setFormData({ ...formData, crossfade_duration: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 12 }}
            />
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.site_id || createMutation.isPending || updateMutation.isPending}
          >
            {editingZone ? 'Save Changes' : 'Create Zone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Zone Dialog */}
      {duplicatingZone && (
        <DuplicateZoneDialog
          open={duplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setDuplicatingZone(null);
          }}
          zoneId={duplicatingZone.id}
          zoneName={duplicatingZone.name}
          currentSiteId={duplicatingZone.site_id}
          onDuplicate={handleDuplicate}
        />
      )}
    </Box>
  );
};

export default ZonesPage;
